// src/pages/account/MyAccount.tsx
import React, { useEffect, useState } from "react";
import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import Field from "@/components/shared/ui/Field";
import Input from "@/components/shared/ui/Input";
import Select from "@/components/shared/ui/Select";
import LocationRow, {
  type LocationFormValue as CSCFormValue,
} from "@/components/shared/forms/LocationRow";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiCalendar,
  FiHash,
  FiHome,
  FiMapPin,
  FiCamera,
  FiShield,
} from "react-icons/fi";

import { useMyAccount, useUpdateMyAccount } from "@/api/features/account";
import type { Gender, IdType, OtpChannel } from "@/api/features/users/types";
import type { MyAccountResult } from "@/api/features/account/types";
import { useUploadImage } from "@/api/features/uploads";

import { useMutation } from "@tanstack/react-query";

import { updateMyAccount } from "@/api/features/account";
import type { UpdateMyAccountBody } from "@/api/features/account/types";

/* =========================
 * Local form type
 * ========================= */

type MyAccountForm = {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phone: string;

  gender?: Gender;
  dob?: string; // yyyy-mm-dd

  idType?: IdType;
  idNumber?: string;

  addressLine1?: string;
  addressLine2?: string;
  country?: string;
  state?: string;
  city?: string;

  otpChannel?: OtpChannel;
  avatarUrl?: string;
};

export default function MyAccount() {
  const {
    data: me,
    isLoading,
    isError,
    refetch,
  } = useMyAccount();
  const updateMutation = useUpdateMyAccount();
    // Separate mutation: auto-save avatar after upload
  const avatarSaveMutation = useMutation({
    mutationFn: (avatarUrl: string) =>
      updateMyAccount({ avatarUrl } as UpdateMyAccountBody),
  });

  
  const uploadAvatarMutation = useUploadImage();
  const [avatarUploadError, setAvatarUploadError] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const [form, setForm] = useState<MyAccountForm | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  /* =========================
   * Init form from API data
   * ========================= */

  useEffect(() => {
    if (!me) return;

    const fromApi = mapMyAccountResultToForm(me);
    setForm(fromApi);

    setLocalError(null);
    setServerError(null);
    setSuccessMessage(null);
  }, [me]);

  const change = <K extends keyof MyAccountForm>(
    key: K,
    value: MyAccountForm[K]
  ) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    if (localError) setLocalError(null);
    if (serverError) setServerError(null);
    if (successMessage) setSuccessMessage(null);
  };

  const resetFromServer = () => {
    if (!me) return;
    const fromApi = mapMyAccountResultToForm(me);
    setForm(fromApi);
    setLocalError(null);
    setServerError(null);
    setSuccessMessage(null);
  };

  /* =========================
   * Submit
   * ========================= */
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;

    // Basic client-side validation
    if (
      !form.firstName.trim() ||
      !form.lastName.trim() ||
      !form.username.trim() ||
      !form.email.trim()
    ) {
      setLocalError(
        "Please fill in first name, last name, username, and email."
      );
      return;
    }

    setLocalError(null);
    setServerError(null);
    setSuccessMessage(null);

    const payload = mapFormToUpdateBody(form);

    updateMutation.mutate(payload, {
      onSuccess: () => {
        setSuccessMessage("Your profile has been updated.");
      },
      onError: (err: any) => {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to update your profile.";
        setServerError(message);
      },
    });
  };

  const triggerAvatarFilePicker = () => {
    setAvatarUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !form) return;

    setAvatarUploadError(null);
    setServerError(null);
    setSuccessMessage(null);

    uploadAvatarMutation.mutate(
      {
        file,
        module: "users",   // 🔹 generic uploads: users
        tag: "profiles",   // 🔹 subfolder: profiles → assets/users/profiles/uuid.ext
      },
      {
        onSuccess(result) {
          if (!result?.url) return;
          const url = result.url;

          // 1. Update local form → instant preview
          setForm((prev) =>
            prev
              ? {
                  ...prev,
                  avatarUrl: url,
                }
              : prev
          );

          // 2. Auto-save to backend so refresh won't lose it
          avatarSaveMutation.mutate(url, {
            onError(err: any) {
              const msg =
                (err as any)?.message || "Failed to save profile picture.";
              setAvatarUploadError(msg);
            },
            onSuccess() {
              // optional: show a small banner or reuse your successMessage:
              setSuccessMessage("Your profile picture has been updated.");
            },
          });
        },
        onError(err: any) {
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            "Failed to upload avatar image.";
          setAvatarUploadError(msg);
        },
      }
    );

    // Reset input so selecting the same file again will still trigger change
    e.target.value = "";
  };


  const bannerMessage = localError || serverError || successMessage;
  const bannerVariant =
    localError || serverError ? "error" : successMessage ? "success" : null;

  const isSaving = updateMutation.isPending || avatarSaveMutation.isPending;
  const isDisabled = !form || isSaving;

  /* =========================
   * Avatar + derived view bits
   * ========================= */

  const initials =
    form && (form.firstName || form.lastName)
      ? `${(form.firstName || "").charAt(0)}${(form.lastName || "").charAt(0)}`
          .toUpperCase()
          .trim()
      : "U";

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">My Account</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>
      </div>

      {/* Loading / error shells */}
      {isLoading && !form && (
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-6 text-sm text-gray-500">
          Loading your account…
        </div>
      )}

      {isError && !form && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
          Failed to load your account.{" "}
          <button
            type="button"
            onClick={() => refetch()}
            className="ml-1 text-sm font-medium text-red-800 underline"
          >
            Retry
          </button>
        </div>
      )}

      {form && (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* LEFT: Avatar + summary */}
          <section className="lg:col-span-1">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              {/* Banner */}
              {bannerMessage && (
                <div
                  className={`mb-4 rounded-lg border px-3 py-2 text-sm ${
                    bannerVariant === "error"
                      ? "border-red-200 bg-red-50 text-red-700"
                      : bannerVariant === "success"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-gray-200 bg-gray-50 text-gray-700"
                  }`}
                  role="alert"
                  aria-live="polite"
                >
                  {bannerMessage}
                </div>
              )}

              {/* Avatar */}
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-3">
                  {form.avatarUrl ? (
                    <img
                      src={form.avatarUrl}
                      alt={`${form.firstName} ${form.lastName}`}
                      className="h-24 w-24 rounded-full object-cover ring-2 ring-sky-100"
                    />
                  ) : (
                    <div className="flex h-24 w-24 items-center justify-center rounded-full bg-sky-100 text-2xl font-semibold text-sky-700 ring-2 ring-sky-100">
                      {initials}
                    </div>
                  )}

                  {/* Loader overlay while uploading */}
                  {uploadAvatarMutation.isPending && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    </div>
                  )}

                  {/* Hidden file input for avatar upload */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarFileChange}
                  />

                  {/* Camera button → open file picker */}
                  <button
                    type="button"
                    onClick={triggerAvatarFilePicker}
                    className="absolute bottom-0 right-0 inline-flex h-7 w-7 items-center justify-center rounded-full border border-white bg-sky-600 text-white shadow-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
                    disabled={uploadAvatarMutation.isPending || isSaving}
                  >
                    <FiCamera className="h-4 w-4" />
                  </button>
                </div>

                <h2 className="text-base font-semibold text-gray-900">
                  {form.firstName} {form.lastName}
                </h2>
                <p className="mt-0.5 text-xs text-gray-500">@{form.username}</p>
              </div>

              {/* Avatar URL field */}
              <div className="mt-5 border-t border-gray-100 pt-4 text-sm">
                <Field label="Avatar URL">
                  <Input
                    value={form.avatarUrl ?? ""}
                    onChange={(e) => change("avatarUrl", e.target.value)}
                    placeholder="https://cdn.tranzytours.com/avatars/you.png"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Click the camera icon above to upload a new photo, or paste a public image URL here.
                  </p>
                  {avatarUploadError && (
                    <p className="mt-1 text-xs text-red-600">{avatarUploadError}</p>
                  )}
                </Field>
              </div>


            </div>
          </section>

          {/* RIGHT: Form */}
          <section className="lg:col-span-2">
            <form
              onSubmit={submit}
              className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
            >
              {/* Personal Info */}
              <div className="mb-4 flex items-center justify-between gap-2">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">
                    Personal information
                  </h2>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Update your basic details and contact information.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* Name */}
                <Field label="First name" icon={<FiUser />}>
                  <Input
                    value={form.firstName}
                    onChange={(e) => change("firstName", e.target.value)}
                    placeholder="Enter first name"
                    required
                  />
                </Field>
                <Field label="Last name" icon={<FiUser />}>
                  <Input
                    value={form.lastName}
                    onChange={(e) => change("lastName", e.target.value)}
                    placeholder="Enter last name"
                    required
                  />
                </Field>

                {/* Username */}
                <Field label="Username" icon={<FiUser />}>
                  <Input
                    value={form.username}
                    onChange={(e) => change("username", e.target.value)}
                    placeholder="Unique username"
                    required
                  />
                </Field>

                {/* Email */}
                <Field label="Email" icon={<FiMail />}>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => change("email", e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </Field>

                {/* Phone */}
                <Field label="Phone" icon={<FiPhone />}>
                  <Input
                    value={form.phone}
                    onChange={(e) => change("phone", e.target.value)}
                    placeholder="+1 555 000 0000"
                  />
                </Field>

                {/* Gender */}
                <Field label="Gender" icon={<FiUser />}>
                  <Select
                    value={form.gender || "Male"}
                    onChange={(e) =>
                      change("gender", e.target.value as Gender)
                    }
                    options={["Male", "Female", "Other"]}
                  />
                </Field>

                {/* DOB */}
                <Field label="Date of birth" icon={<FiCalendar />}>
                  <Input
                    type="date"
                    value={form.dob || ""}
                    onChange={(e) => change("dob", e.target.value)}
                    placeholder="YYYY-MM-DD"
                  />
                </Field>

                {/* ID */}
                <Field label="Identification type" icon={<FiHash />}>
                  <Select
                    value={form.idType || "National ID"}
                    onChange={(e) =>
                      change("idType", e.target.value as IdType)
                    }
                    options={["National ID", "Passport"]}
                  />
                </Field>
                <Field label="ID number" icon={<FiHash />}>
                  <Input
                    value={form.idNumber || ""}
                    onChange={(e) => change("idNumber", e.target.value)}
                    placeholder="Enter ID / Passport no."
                  />
                </Field>

                {/* OTP channel */}
                <Field label="Preferred OTP channel" icon={<FiShield />}>
                  <Select
                    value={form.otpChannel || "BOTH"}
                    onChange={(e) =>
                      change("otpChannel", e.target.value as OtpChannel)
                    }
                    options={["SMS", "EMAIL", "BOTH"]}
                  />
                </Field>
              </div>

              {/* Address */}
              <div className="mt-6 border-t border-gray-100 pt-4">
                <div className="mb-3 text-sm font-semibold text-gray-800">
                  Address &amp; Location
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Address line 1" icon={<FiHome />}>
                    <Input
                      value={form.addressLine1 || ""}
                      onChange={(e) =>
                        change("addressLine1", e.target.value)
                      }
                      placeholder="House / Flat / Street"
                    />
                  </Field>
                  <Field label="Address line 2" icon={<FiMapPin />}>
                    <Input
                      value={form.addressLine2 || ""}
                      onChange={(e) =>
                        change("addressLine2", e.target.value)
                      }
                      placeholder="Area / Landmark (optional)"
                    />
                  </Field>
                </div>

                <div className="mt-3">
                  <LocationRow
                    value={{
                      country: form.country || "",
                      state: form.state || "",
                      city: form.city || "",
                    }}
                    onChange={(v: CSCFormValue) => {
                      setForm((prev) =>
                        prev
                          ? {
                              ...prev,
                              country: v.country || "",
                              state: v.state || "",
                              city: v.city || "",
                            }
                          : prev
                      );
                      if (localError) setLocalError(null);
                      if (serverError) setServerError(null);
                      if (successMessage) setSuccessMessage(null);
                    }}
                    instancePrefix="my-account-csc"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={resetFromServer}
                  disabled={isDisabled}
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
                  disabled={isDisabled}
                >
                  {isSaving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </AdminLayout>
  );
}

/* =========================
 * Mapping helpers
 * ========================= */

function mapMyAccountResultToForm(me: MyAccountResult): MyAccountForm {
  return {
    firstName: me.firstName ?? "",
    lastName: me.lastName ?? "",
    username: me.username ?? "",
    email: me.email ?? "",
    phone: me.phone ?? "",

    gender: me.gender ?? undefined,
    dob: me.dob ? me.dob.slice(0, 10) : "",

    idType: me.idType ?? undefined,
    idNumber: me.idNumber ?? undefined,

    addressLine1: me.addressLine1 ?? undefined,
    addressLine2: me.addressLine2 ?? undefined,
    country: me.country ?? undefined,
    state: me.state ?? undefined,
    city: me.city ?? undefined,

    otpChannel: me.otpChannel ?? undefined,
    avatarUrl: me.avatarUrl ?? undefined,
  };
}

function mapFormToUpdateBody(form: MyAccountForm) {
  return {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    username: form.username.trim(),
    email: form.email.trim(),

    phone: form.phone?.trim() || undefined,

    gender: form.gender ?? null,
    dob: form.dob && form.dob.trim() ? form.dob : null,

    idType: form.idType ?? null,
    idNumber: form.idNumber?.trim() || null,

    addressLine1: form.addressLine1?.trim() || null,
    addressLine2: form.addressLine2?.trim() || null,
    country: form.country?.trim() || null,
    state: form.state?.trim() || null,
    city: form.city?.trim() || null,

    otpChannel: form.otpChannel ?? null,
    avatarUrl: form.avatarUrl?.trim() || null,
  };
}
